/**
 * Odds Store - Centralized state for all odds data
 * ================================================
 * Last Modified: 2025-12-08 5:10 PM MST
 *
 * PERFORMANCE FIX: Added batchUpdateOdds to avoid O(n²) state updates
 * Previously each of 13,000+ items called set() individually, causing
 * 13,000 re-renders and 11+ second processing times.
 */
import { create } from 'zustand'

export const useOddsStore = create((set, get) => ({
  isConnected: false,
  setConnected: (connected) => set({ isConnected: connected }),

  oddsData: {},

  // Single item update (kept for incremental updates)
  updateOdds: (fixtureId, data) =>
    set((state) => ({
      oddsData: {
        ...state.oddsData,
        [fixtureId]: {
          ...state.oddsData[fixtureId],
          ...data,
          lastUpdated: Date.now()
        }
      }
    })),

  // BATCH UPDATE - Critical for performance!
  // Processes all items in a single set() call instead of 13,000+ calls
  batchUpdateOdds: (items) => {
    if (!items || items.length === 0) return

    set((state) => {
      // Build new oddsData object with all updates
      const newOddsData = { ...state.oddsData }
      const now = Date.now()

      items.forEach((item) => {
        // Generate compound key (same as useWebSocket.js)
        const key = item.canonical_id + '-' + item.bookmaker + '-' +
          (item.market_normalized || item.market) + '-' +
          item.selection + '-' + (item.handicap || '')

        newOddsData[key] = {
          ...newOddsData[key],
          ...item,
          lastUpdated: now
        }
      })

      return { oddsData: newOddsData }
    })
  },

  setOddsData: (data) => set({ oddsData: data }),

  clearOdds: () => set({ oddsData: {} }),

  // Clear odds for a specific sport/league - removes stale data when bookmakers remove games
  clearOddsBySport: (sport) => {
    set((state) => {
      const newOddsData = {}
      const targetSport = sport.toUpperCase()
      Object.entries(state.oddsData).forEach(([key, item]) => {
        const itemSport = (item.league || item.sport || '').toUpperCase()
        if (itemSport !== targetSport) {
          newOddsData[key] = item // Keep data from other sports
        }
      })
      return { oddsData: newOddsData }
    })
  },

  updateCount: 0,
  incrementUpdateCount: () =>
    set((state) => ({ updateCount: state.updateCount + 1 })),

  lastSync: null,
  setLastSync: (timestamp) => set({ lastSync: timestamp }),

  getOddsBySport: (sport) => {
    const state = get()
    return Object.values(state.oddsData).filter(
      (item) => item.sport?.toLowerCase() === sport.toLowerCase()
    )
  },

  getAllFixtures: () => {
    const state = get()
    return Object.values(state.oddsData)
  }
}))
